import React, { useState, useEffect, useMemo } from "react";
import { installmentsService } from "../services/api";
import {
  User,
  Banknote,
  Calendar as CalendarIcon,
  Check,
  AlertCircle,
  Image as ImageIcon,
  ChevronDown,
  ChevronUp,
  HelpCircle,
  X,
  Eye,
} from "lucide-react";
import {
  formatCurrency,
  toPersianNumbers,
  toEnglishNumbers,
} from "../utils/numbers";
import { Modal } from "../components/Modal";

const getImageUrl = (url: string | undefined | null) => {
  if (!url) return "";
  const baseUrl = (import.meta as any).env?.VITE_API_BASE_URL || "";
  if (baseUrl && url.startsWith("/api/")) {
    return baseUrl.replace(/\/api\/?$/, "") + url;
  }
  return url;
};

const formatDatePersian = (dueDateStr: string) => {
  if (!dueDateStr) return "";
  try {
    const dateObj = new Date(dueDateStr);
    return new Intl.DateTimeFormat("fa-IR").format(dateObj);
  } catch (e) {
    return dueDateStr;
  }
};

export default function Installments() {
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const isAdmin = user?.role === "admin";

  const [installments, setInstallments] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  const [expandedGroups, setExpandedGroups] = useState<string[]>([]);

  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [selectedInstallment, setSelectedInstallment] = useState<any>(null);
  const [penaltyAmount, setPenaltyAmount] = useState("");
  const [isReceiptModalOpen, setIsReceiptModalOpen] = useState(false);
  const [viewReceiptImage, setViewReceiptImage] = useState<string | null>(null);
  const [isApproving, setIsApproving] = useState(false);

  const [modalState, setModalState] = useState({
    isOpen: false,
    title: "",
    message: "",
    type: "success" as "success" | "error" | "warning",
  });

  const fetchInstallments = async () => {
    setLoading(true);
    try {
      const response = await installmentsService.getAdminInstallments();
      const data = Array.isArray(response) ? response : response.data || [];
      setInstallments(data);
    } catch (error) {
      console.error("Error fetching installments:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInstallments();
  }, []);

  const calculateDelayDays = (dueDateStr: string) => {
    const today = new Date();
    const dueDate = new Date(dueDateStr);
    today.setHours(0, 0, 0, 0);
    dueDate.setHours(0, 0, 0, 0);

    if (today <= dueDate) return 0;

    const diffTime = Math.abs(today.getTime() - dueDate.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const calculateDefaultPenalty = (days: number) => {
    return days * 5000000;
  };

  const handleApproveClick = (inst: any) => {
    setSelectedInstallment(inst);
    const days = calculateDelayDays(inst.due_date);
    if (days > 0) {
      const defaultPenalty = calculateDefaultPenalty(days);
      setPenaltyAmount(defaultPenalty.toString());
    } else {
      setPenaltyAmount("0");
    }
    setIsConfirmModalOpen(true);
  };

  const approveInstallment = async (id: number, penalty: number) => {
    setIsApproving(true);
    try {
      await installmentsService.approveInstallment(id, {
        penalty_amount: penalty,
      });
      setInstallments((prev) =>
        prev.map((inst) =>
          inst.id === id
            ? { ...inst, status: "paid", penalty_amount: penalty }
            : inst,
        ),
      );
      setIsConfirmModalOpen(false);
      setSelectedInstallment(null);
      setModalState({
        isOpen: true,
        title: "موفقیت",
        message: "پرداخت قسط با موفقیت تایید و در سامانه ثبت گردید.",
        type: "success",
      });
    } catch (error: any) {
      setModalState({
        isOpen: true,
        title: "خطا",
        message: error.response?.data?.message || "خطا در تایید قسط",
        type: "error",
      });
    } finally {
      setIsApproving(false);
    }
  };

  const handleConfirmSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedInstallment) return;

    const days = calculateDelayDays(selectedInstallment.due_date);
    let finalPenalty = 0;
    if (days > 0) {
      const englishPenalty = toEnglishNumbers(penaltyAmount).replace(/,/g, "");
      finalPenalty = Number(englishPenalty) || 0;
    }
    approveInstallment(selectedInstallment.id, finalPenalty);
  };

  const handlePenaltyChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = toEnglishNumbers(e.target.value).replace(/,/g, "");
    if (!isNaN(Number(rawValue))) {
      setPenaltyAmount(rawValue);
    }
  };

  const toggleGroup = (sortKey: string) => {
    setExpandedGroups((prev) =>
      prev.includes(sortKey)
        ? prev.filter((k) => k !== sortKey)
        : [...prev, sortKey],
    );
  };

  const groupedInstallments = useMemo(() => {
    const groups: Record<
      string,
      {
        sortKey: string;
        title: string;
        items: any[];
        totalAmount: number;
        allPaid: boolean;
      }
    > = {};

    installments.forEach((inst) => {
      const dateObj = new Date(inst.due_date);
      let monthName = "";
      let year = "";
      try {
        monthName = new Intl.DateTimeFormat("fa-IR", { month: "long" }).format(
          dateObj,
        );
        year = new Intl.DateTimeFormat("fa-IR", { year: "numeric" }).format(
          dateObj,
        );
      } catch (e) {
        monthName = "نامشخص";
        year = "";
      }
      const title = `${monthName} ${year}`;
      let sortKey = "";
      try {
        // Using standard fa-IR which correctly outputs Jalaali dates e.g., '1404/02'
        sortKey = new Intl.DateTimeFormat("fa-IR", {
          year: "numeric",
          month: "2-digit",
        }).format(dateObj);
      } catch (e) {
        sortKey = inst.due_date.substring(0, 7);
      }

      if (!groups[sortKey]) {
        groups[sortKey] = {
          sortKey,
          title,
          items: [],
          totalAmount: 0,
          allPaid: true,
        };
      }
      groups[sortKey].items.push(inst);
      groups[sortKey].totalAmount += Number(inst.amount) || 0;
      if (inst.status !== "paid") {
        groups[sortKey].allPaid = false;
      }
    });

    return Object.values(groups).sort((a, b) => {
      if (a.allPaid && !b.allPaid) return 1;
      if (!a.allPaid && b.allPaid) return -1;
      return a.sortKey.localeCompare(b.sortKey);
    });
  }, [installments]);

  // Auto-expand first non-paid group if none expanded
  useEffect(() => {
    if (groupedInstallments.length > 0 && expandedGroups.length === 0) {
      const firstActive = groupedInstallments.find((g) => !g.allPaid);
      if (firstActive) {
        setExpandedGroups([firstActive.sortKey]);
      }
    }
  }, [groupedInstallments]);

  const { unpaidCount, unpaidTotalAmount } = useMemo(() => {
    let count = 0;
    let total = 0;
    installments.forEach((inst) => {
      if (inst.status !== "paid") {
        count++;
        total += Number(inst.amount) || 0;
      }
    });
    return { unpaidCount: count, unpaidTotalAmount: total };
  }, [installments]);

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-700 p-8 flex flex-col justify-center">
          <div className="flex items-center justify-between mb-2">
            <h1 className="text-2xl font-bold text-slate-900 dark:text-white">
              مدیریت اقساط
            </h1>
            {!isAdmin && (
              <span className="px-3 py-1 bg-amber-50 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 border border-amber-200 dark:border-amber-800 rounded-xl text-xs font-semibold">
                حالت فقط مشاهده (سرمایه‌گذار)
              </span>
            )}
          </div>
          <p className="text-slate-600 dark:text-slate-300">
            مشاهده اقساط، فیش‌های واریزی و محاسبه دیرکرد
          </p>
        </div>
        <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-700 p-8 flex flex-col justify-center">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-medium text-slate-700 dark:text-slate-300">
              اقساط پرداخت نشده
            </h2>
            <div className="w-12 h-12 bg-orange-50 text-orange-600 dark:bg-orange-900/30 dark:text-orange-400 rounded-2xl flex items-center justify-center">
              <Banknote className="w-6 h-6" />
            </div>
          </div>
          <div className="flex items-baseline gap-2 mb-2">
            <span className="text-3xl font-bold text-slate-900 dark:text-white">
              {toPersianNumbers(unpaidCount)}
            </span>
            <span className="text-slate-500 dark:text-slate-400 font-medium">
              قسط
            </span>
          </div>
          <div className="flex items-center gap-2 text-slate-600 dark:text-slate-400">
            <span>جمع مبالغ:</span>
            <span className="font-bold text-orange-600 dark:text-orange-400 text-lg">
              {formatCurrency(unpaidTotalAmount)}
            </span>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-700 p-12 text-center text-slate-500 dark:text-slate-400">
          <div className="flex flex-col items-center justify-center space-y-4">
            <div className="w-8 h-8 border-4 border-persian-600 border-t-transparent rounded-full animate-spin"></div>
            <p>در حال دریافت اطلاعات...</p>
          </div>
        </div>
      ) : groupedInstallments.length === 0 ? (
        <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-700 p-12 text-center text-slate-500 dark:text-slate-400">
          <div className="flex flex-col items-center justify-center space-y-3">
            <AlertCircle className="w-12 h-12 text-slate-300 dark:text-slate-600" />
            <p>هیچ قسطی یافت نشد.</p>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          {groupedInstallments.map((group) => {
            const isExpanded = expandedGroups.includes(group.sortKey);
            return (
              <div
                key={group.sortKey}
                className={`bg-white dark:bg-slate-800 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-700 overflow-hidden transition-all duration-200 ${group.allPaid ? "opacity-80" : ""}`}
              >
                <div
                  onClick={() => toggleGroup(group.sortKey)}
                  className={`p-5 flex flex-wrap items-center justify-between gap-4 cursor-pointer transition-colors hover:bg-slate-50 dark:hover:bg-slate-700/50 ${isExpanded ? "border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-700/30" : ""}`}
                >
                  <div className="flex items-center gap-4 sm:gap-8 flex-wrap">
                    <h3 className="text-lg font-bold text-slate-800 dark:text-white flex items-center gap-2">
                      <CalendarIcon className="w-5 h-5 text-persian-500" />
                      {group.title}
                    </h3>
                    <div className="hidden sm:block w-px h-6 bg-slate-200 dark:bg-slate-700"></div>
                    <div className="flex items-center gap-1.5 text-slate-600 dark:text-slate-300">
                      <span className="font-medium">
                        {toPersianNumbers(group.items.length)}
                      </span>
                      <span>قسط</span>
                    </div>
                    <div className="hidden sm:block w-px h-6 bg-slate-200 dark:bg-slate-700"></div>
                    <div className="flex items-center gap-2 text-slate-600 dark:text-slate-300">
                      <span>جمع اقساط این ماه:</span>
                      <span className="font-bold text-persian-600 dark:text-persian-400">
                        {formatCurrency(group.totalAmount)}
                      </span>
                    </div>
                  </div>

                  <div className="flex items-center gap-4">
                    {group.allPaid && (
                      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-green-50 text-green-700 dark:bg-green-900/30 dark:text-green-400">
                        <Check className="w-4 h-4" /> تسویه شده
                      </span>
                    )}
                    <button className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 transition-colors p-1">
                      {isExpanded ? (
                        <ChevronUp className="w-5 h-5" />
                      ) : (
                        <ChevronDown className="w-5 h-5" />
                      )}
                    </button>
                  </div>
                </div>

                {isExpanded && (
                  <div className="overflow-x-auto">
                    <table className="w-full text-right border-collapse whitespace-nowrap text-sm">
                      <thead>
                        <tr className="bg-slate-50/50 dark:bg-slate-900/30 border-b border-slate-200 dark:border-slate-700">
                          <th className="p-4 font-semibold text-slate-700 dark:text-slate-300">
                            متقاضی
                          </th>
                          <th className="p-4 font-semibold text-slate-700 dark:text-slate-300">
                            کد وام
                          </th>
                          <th className="p-4 font-semibold text-slate-700 dark:text-slate-300">
                            شماره قسط
                          </th>
                          <th className="p-4 font-semibold text-slate-700 dark:text-slate-300">
                            مبلغ قسط
                          </th>
                          <th className="p-4 font-semibold text-slate-700 dark:text-slate-300">
                            تاریخ سررسید
                          </th>
                          <th className="p-4 font-semibold text-slate-700 dark:text-slate-300">
                            وضعیت دیرکرد
                          </th>
                          <th className="p-4 font-semibold text-slate-700 dark:text-slate-300">
                            عملیات
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
                        {group.items.map((inst) => {
                          const delayDays = calculateDelayDays(inst.due_date);
                          const isPaid = inst.status === "paid";

                          let persianDateStr = inst.due_date;
                          try {
                            const dateObj = new Date(inst.due_date);
                            persianDateStr = new Intl.DateTimeFormat(
                              "fa-IR",
                            ).format(dateObj);
                          } catch (e) {}

                          return (
                            <tr
                              key={inst.id}
                              className={`${isPaid ? "bg-green-50/40 dark:bg-green-900/10" : "hover:bg-slate-50 dark:hover:bg-slate-800/50"} transition-colors`}
                            >
                              <td className="p-4 font-medium text-slate-900 dark:text-white">
                                <div className="flex items-center gap-2">
                                  <User className="w-4 h-4 text-slate-400" />
                                  {inst.first_name} {inst.last_name}
                                </div>
                              </td>
                              <td className="p-4 text-slate-600 dark:text-slate-300">
                                <bdi
                                  dir="ltr"
                                  className="inline-block font-bold"
                                >
                                  {toPersianNumbers(inst.loan_code)}
                                </bdi>
                              </td>
                              <td className="p-4 text-slate-600 dark:text-slate-300">
                                <bdi
                                  dir="ltr"
                                  className="inline-block font-bold"
                                >
                                  {toPersianNumbers(inst.installment_code)}
                                </bdi>
                              </td>
                              <td className="p-4">
                                <div className="flex items-center gap-1 text-slate-700 dark:text-slate-300 font-medium">
                                  <Banknote className="w-4 h-4 text-slate-400" />
                                  {formatCurrency(inst.amount)}
                                </div>
                              </td>
                              <td className="p-4">
                                <div className="flex items-center gap-2 text-slate-700 dark:text-slate-300">
                                  <CalendarIcon className="w-4 h-4 text-slate-400" />
                                  {toPersianNumbers(persianDateStr)}
                                </div>
                              </td>
                              <td className="p-4">
                                {isPaid ? (
                                  <span className="text-slate-400">-</span>
                                ) : delayDays > 0 ? (
                                  <div className="flex flex-col gap-1 items-start">
                                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium bg-red-50 text-red-600 dark:bg-red-900/20 dark:text-red-400 border border-red-200 dark:border-red-800">
                                      {toPersianNumbers(delayDays)} روز دیرکرد
                                    </span>
                                    <span className="text-xs font-medium text-red-600 dark:text-red-400">
                                      مبلغ:{" "}
                                      {formatCurrency(
                                        calculateDefaultPenalty(delayDays),
                                      )}{" "}
                                      ریال
                                    </span>
                                  </div>
                                ) : (
                                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium bg-green-50 text-green-600 dark:bg-green-900/20 dark:text-green-400 border border-green-200 dark:border-green-800">
                                    بدون دیرکرد
                                  </span>
                                )}
                              </td>
                              <td className="p-4 flex items-center gap-2">
                                {isPaid ? (
                                  <span className="inline-flex items-center gap-1 text-green-600 dark:text-green-400 font-medium px-3 py-1.5">
                                    <Check className="w-4 h-4" /> تایید شده
                                  </span>
                                ) : (
                                  <>
                                    {inst.receipt_image && (
                                      <button
                                        onClick={() => {
                                          setViewReceiptImage(
                                            inst.receipt_image,
                                          );
                                          setIsReceiptModalOpen(true);
                                        }}
                                        className="flex items-center gap-1.5 px-3 py-1.5 bg-indigo-50 text-indigo-600 dark:bg-indigo-900/20 dark:text-indigo-400 hover:bg-indigo-100 dark:hover:bg-indigo-900/40 rounded-lg text-sm font-medium transition-colors"
                                      >
                                        <ImageIcon className="w-4 h-4" />
                                        <span>مشاهده فیش</span>
                                      </button>
                                    )}
                                    {isAdmin ? (
                                      <button
                                        onClick={() => handleApproveClick(inst)}
                                        className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 text-blue-600 dark:bg-blue-900/20 dark:text-blue-400 hover:bg-blue-100 dark:hover:bg-blue-900/40 rounded-lg text-sm font-medium transition-colors"
                                      >
                                        <Check className="w-4 h-4" />
                                        <span>تایید پرداخت</span>
                                      </button>
                                    ) : (
                                      <span className="text-xs text-slate-400 font-medium px-2 py-1">
                                        در انتظار تایید پرداخت
                                      </span>
                                    )}
                                  </>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Installment Payment Confirmation Modal */}
      {isConfirmModalOpen &&
        selectedInstallment &&
        (() => {
          const delayDays = calculateDelayDays(selectedInstallment.due_date);
          const parsedPenalty =
            delayDays > 0
              ? Number(
                  toEnglishNumbers(penaltyAmount || "0").replace(/,/g, ""),
                ) || 0
              : 0;
          const totalPayable =
            Number(selectedInstallment.amount || 0) + parsedPenalty;

          return (
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-in fade-in duration-200">
              <div className="bg-white dark:bg-slate-800 rounded-3xl shadow-2xl border border-slate-200 dark:border-slate-700 w-full max-w-lg overflow-hidden flex flex-col max-h-[90vh] animate-in zoom-in-95 duration-200">
                {/* Header */}
                <div
                  className={`p-5 border-b flex items-center justify-between ${
                    delayDays > 0
                      ? "bg-amber-50 dark:bg-amber-950/30 border-amber-200 dark:border-amber-900/50"
                      : "bg-blue-50 dark:bg-blue-950/30 border-blue-200 dark:border-blue-900/50"
                  }`}
                >
                  <div className="flex items-center gap-3">
                    {delayDays > 0 ? (
                      <AlertCircle className="w-6 h-6 text-amber-600 dark:text-amber-400 shrink-0" />
                    ) : (
                      <HelpCircle className="w-6 h-6 text-blue-600 dark:text-blue-400 shrink-0" />
                    )}
                    <div>
                      <h2
                        className={`text-lg font-bold ${
                          delayDays > 0
                            ? "text-amber-950 dark:text-amber-200"
                            : "text-blue-950 dark:text-blue-200"
                        }`}
                      >
                        {delayDays > 0
                          ? "تایید پرداخت قسط (با دیرکرد)"
                          : "تایید پرداخت قسط"}
                      </h2>
                      <p className="text-xs text-slate-500 dark:text-slate-400">
                        بررسی اطلاعات و اخذ تاییدیه نهایی قبل از ثبت پرداخت در
                        دیتابیس
                      </p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      if (!isApproving) {
                        setIsConfirmModalOpen(false);
                        setSelectedInstallment(null);
                      }
                    }}
                    className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 hover:bg-slate-200/50 dark:hover:bg-slate-700/50 transition-colors"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>

                {/* Body */}
                <form
                  onSubmit={handleConfirmSubmit}
                  className="p-6 overflow-y-auto space-y-4"
                >
                  {/* Confirmation Alert */}
                  <div
                    className={`p-3.5 rounded-2xl border text-xs sm:text-sm leading-relaxed ${
                      delayDays > 0
                        ? "bg-amber-50 dark:bg-amber-950/40 border-amber-200 dark:border-amber-800 text-amber-900 dark:text-amber-200"
                        : "bg-blue-50 dark:bg-blue-950/40 border-blue-200 dark:border-blue-800 text-blue-900 dark:text-blue-200"
                    }`}
                  >
                    آیا از تایید و ثبت پرداخت این قسط اطمینان دارید؟ با تایید
                    نهایی، وضعیت قسط به <strong>«تسویه شده»</strong> تغییر یافته
                    و در سوابق ذخیره خواهد شد.
                  </div>

                  {/* Details Card */}
                  <div className="bg-slate-50 dark:bg-slate-900/60 p-4 rounded-2xl border border-slate-200 dark:border-slate-700 space-y-2.5 text-xs sm:text-sm">
                    <div className="flex justify-between items-center py-1 border-b border-slate-200/80 dark:border-slate-800">
                      <span className="text-slate-500 dark:text-slate-400">
                        نام متقاضی:
                      </span>
                      <span className="font-bold text-slate-900 dark:text-white">
                        {selectedInstallment.first_name}{" "}
                        {selectedInstallment.last_name}
                      </span>
                    </div>

                    {selectedInstallment.national_code && (
                      <div className="flex justify-between items-center py-1 border-b border-slate-200/80 dark:border-slate-800">
                        <span className="text-slate-500 dark:text-slate-400">
                          کد ملی:
                        </span>
                        <span className="font-semibold text-slate-800 dark:text-slate-200">
                          {toPersianNumbers(selectedInstallment.national_code)}
                        </span>
                      </div>
                    )}

                    <div className="flex justify-between items-center py-1 border-b border-slate-200/80 dark:border-slate-800">
                      <span className="text-slate-500 dark:text-slate-400">
                        کد وام / شماره قسط:
                      </span>
                      <div className="flex items-center gap-2 font-semibold text-slate-800 dark:text-slate-200 text-xs sm:text-sm">
                        <span className="inline-flex items-center gap-1">
                          <span className="text-slate-500 dark:text-slate-400 font-normal">
                            وام
                          </span>
                          <bdi
                            dir="ltr"
                            className="font-bold text-slate-900 dark:text-white font-bold"
                          >
                            {toPersianNumbers(selectedInstallment.loan_code)}
                          </bdi>
                        </span>
                        <span className="text-slate-300 dark:text-slate-600 font-normal">
                          -
                        </span>
                        <span className="inline-flex items-center gap-1">
                          <span className="text-slate-500 dark:text-slate-400 font-normal">
                            قسط
                          </span>
                          <bdi
                            dir="ltr"
                            className="font-bold text-slate-900 dark:text-white font-bold"
                          >
                            {toPersianNumbers(
                              selectedInstallment.installment_code,
                            )}
                          </bdi>
                        </span>
                      </div>
                    </div>

                    <div className="flex justify-between items-center py-1 border-b border-slate-200/80 dark:border-slate-800">
                      <span className="text-slate-500 dark:text-slate-400">
                        تاریخ سررسید:
                      </span>
                      <span
                        className="font-semibold text-slate-800 dark:text-slate-200"
                        dir="ltr"
                      >
                        {toPersianNumbers(
                          formatDatePersian(selectedInstallment.due_date),
                        )}
                      </span>
                    </div>

                    <div className="flex justify-between items-center py-1 border-b border-slate-200/80 dark:border-slate-800">
                      <span className="text-slate-500 dark:text-slate-400">
                        وضعیت تاخیر:
                      </span>
                      <span>
                        {delayDays > 0 ? (
                          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-bold bg-red-100 text-red-700 dark:bg-red-950/60 dark:text-red-300 border border-red-200 dark:border-red-800">
                            {toPersianNumbers(delayDays)} روز دیرکرد
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-bold bg-green-100 text-green-700 dark:bg-green-950/60 dark:text-green-300 border border-green-200 dark:border-green-800">
                            <Check className="w-3.5 h-3.5" /> بدون دیرکرد
                          </span>
                        )}
                      </span>
                    </div>

                    <div className="flex justify-between items-center py-1 border-b border-slate-200/80 dark:border-slate-800">
                      <span className="text-slate-500 dark:text-slate-400">
                        مبلغ اصل قسط:
                      </span>
                      <span className="font-bold text-slate-900 dark:text-white">
                        {formatCurrency(selectedInstallment.amount)}
                      </span>
                    </div>

                    {delayDays > 0 && (
                      <div className="flex justify-between items-center py-1 text-red-600 dark:text-red-400">
                        <span className="font-medium">مبلغ جریمه دیرکرد:</span>
                        <span className="font-bold">
                          {formatCurrency(parsedPenalty)}
                        </span>
                      </div>
                    )}

                    <div className="flex justify-between items-center py-1 text-emerald-600 dark:text-emerald-400 pt-2 border-t border-slate-200 dark:border-slate-700 font-bold">
                      <span>مبلغ کل پرداختی:</span>
                      <span className="text-base">
                        {formatCurrency(totalPayable)}
                      </span>
                    </div>
                  </div>

                  {/* Optional view receipt preview link */}
                  {selectedInstallment.receipt_image && (
                    <div className="flex items-center justify-between p-3 rounded-xl bg-indigo-50 dark:bg-indigo-950/30 border border-indigo-200 dark:border-indigo-800/50">
                      <span className="text-xs text-indigo-900 dark:text-indigo-300 font-medium">
                        فیش واریزی توسط متقاضی بارگذاری شده است:
                      </span>
                      <button
                        type="button"
                        onClick={() => {
                          setViewReceiptImage(
                            selectedInstallment.receipt_image,
                          );
                          setIsReceiptModalOpen(true);
                        }}
                        className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-bold transition-colors"
                      >
                        <Eye className="w-3.5 h-3.5" />
                        مشاهده تصویر فیش
                      </button>
                    </div>
                  )}

                  {/* Delay Penalty Input Field if delayDays > 0 */}
                  {delayDays > 0 && (
                    <div className="space-y-1.5 pt-1">
                      <label className="block text-xs font-bold text-slate-700 dark:text-slate-300">
                        مبلغ دیرکرد قابل تعدیل (ریال):
                      </label>
                      <div className="relative">
                        <input
                          type="text"
                          value={toPersianNumbers(
                            penaltyAmount.replace(/\B(?=(\d{3})+(?!\d))/g, ","),
                          )}
                          onChange={handlePenaltyChange}
                          className="w-full pl-4 pr-10 py-2.5 bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-600 rounded-xl focus:ring-2 focus:ring-persian-500 outline-none text-slate-900 dark:text-white font-bold transition-colors"
                          dir="ltr"
                          required
                        />
                        <Banknote
                          className="absolute right-3 top-3 text-slate-400"
                          size={18}
                        />
                      </div>
                      <p className="text-[11px] text-slate-500 dark:text-slate-400">
                        مبلغ دیرکرد به صورت پیش‌فرض بر اساس روزهای تاخیر محاسبه
                        شده است. در صورت تمایل می‌توانید آن را ویرایش یا صفر
                        کنید.
                      </p>
                    </div>
                  )}

                  {/* Action Buttons */}
                  <div className="flex gap-3 pt-3">
                    <button
                      type="submit"
                      disabled={isApproving}
                      className="flex-1 bg-persian-600 hover:bg-persian-700 text-white h-11 rounded-xl font-bold transition-all shadow-md shadow-persian-600/20 disabled:opacity-50 flex items-center justify-center gap-2"
                    >
                      {isApproving && (
                        <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                      )}
                      <span>
                        {isApproving
                          ? "در حال ثبت پرداخت..."
                          : "بله، تایید و ثبت پرداخت"}
                      </span>
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setIsConfirmModalOpen(false);
                        setSelectedInstallment(null);
                      }}
                      disabled={isApproving}
                      className="flex-1 bg-slate-100 hover:bg-slate-200 text-slate-700 dark:bg-slate-700 dark:hover:bg-slate-600 dark:text-slate-200 h-11 rounded-xl font-medium transition-colors"
                    >
                      انصراف
                    </button>
                  </div>
                </form>
              </div>
            </div>
          );
        })()}

      <Modal
        isOpen={modalState.isOpen}
        onClose={() => setModalState({ ...modalState, isOpen: false })}
        title={modalState.title}
        message={modalState.message}
        type={modalState.type}
      />

      {/* Receipt View Modal */}
      {isReceiptModalOpen && viewReceiptImage && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/80 backdrop-blur-sm"
          onClick={() => setIsReceiptModalOpen(false)}
        >
          <div
            className="bg-white dark:bg-slate-800 rounded-2xl shadow-xl border border-slate-200 dark:border-slate-700 w-full max-w-2xl overflow-hidden relative"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-4 border-b border-slate-200 dark:border-slate-700 flex justify-between items-center">
              <h2 className="text-lg font-bold text-slate-800 dark:text-white">
                مشاهده فیش پرداختی
              </h2>
              <button
                onClick={() => setIsReceiptModalOpen(false)}
                className="text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"
              >
                بستن
              </button>
            </div>
            <div className="p-4 flex justify-center bg-slate-100 dark:bg-slate-900">
              <img
                src={getImageUrl(viewReceiptImage)}
                alt="فیش"
                className="max-w-full max-h-[70vh] object-contain rounded-lg"
              />
            </div>
            <div className="p-4 border-t border-slate-200 dark:border-slate-700 flex justify-end">
              <a
                href={getImageUrl(viewReceiptImage)}
                download
                className="bg-persian-600 hover:bg-persian-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
              >
                دانلود تصویر
              </a>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
